import AddTicketIcon from "@mui/icons-material/AddBoxRounded";
import SyncIcon from "@mui/icons-material/Cached";
import CompleteIcon from "@mui/icons-material/CheckCircle";
import DeleteTicketIcon from "@mui/icons-material/DeleteForever";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import BrokenSync from "@mui/icons-material/ReportOff";
import { Box, Button, ListItemIcon, ListItemText, Select, useMediaQuery, useTheme } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import React, { useContext, useEffect } from "react";
import { GlobalContext } from "../Utils/Contexts";
import TicketValidator, { DUPLICATE_TICKET, FAILED_SYNC, PARTIAL_SYNC } from "../Utils/TicketValidator";
import { TicketExtended } from "../Utils/TypeExtensions";

import { bookmarkTicket, getBookmarkedTicketId, getTicketNumber, HasValue, IsDemoMode, isLastRevision } from "../sharedCode/HelperMethods";
import { ErrorLevel } from "../Utils/Interfaces";
import { MONTH_FILTER_PREFERENCE_KEY } from "../sharedCode/metadata";
import { useLiveQuery } from "dexie-react-hooks";
import { TicketDatabase } from "../Utils/TicketDatabase";
import { createInitializationTicket, isNotInitializationTicket } from "../Utils/UtilityMethods";
import { findById } from "../Utils/ContextLookups";

export const TICKET_DATE_FORMAT = "YYYY/MM/DD";
const MONTH_SELECTION_FORMAT = "YYYY/MM";

enum TicketStatus {
    Complete,
    Incomplete,
    Signed,
    Synced,
    Failed,
    Partial,
    Superseded,
}

export default function TicketNavigator(): JSX.Element {
    const context = useContext(GlobalContext);
    const theme = useTheme();
    const isOnMediumScreen = useMediaQuery(theme.breakpoints.up("md"));
    const aircraftList = useLiveQuery(async () => TicketDatabase.aircraft.toArray());
    const companyList = useLiveQuery(async () => TicketDatabase.company.toArray());
    const ticketList = useLiveQuery(async () => TicketDatabase.ticket.toArray());

    useEffect(() => {
        if (ticketList) {
            const selectedTicketId = getBookmarkedTicketId(ticketList);
            if (selectedTicketId !== context.selectedTicketId) {
                context.setSelectedTicketId(selectedTicketId);
            }
        }
    }, [ticketList, context.selectedTicketId, IsDemoMode()]);

    if (!aircraftList || !companyList || !ticketList) return <></>;

    // const selectedTicket = ticketList.find((t) => t.id === context.selectedTicketId) ?? createInitializationTicket();
    const selectedTicket = ticketList.find((t) => t.id === getBookmarkedTicketId(ticketList)) ?? createInitializationTicket();

    ticketList.sort(TicketSort);

    const STATUS_FORMATS = [
        { textColor: theme.palette.text.primary, hasIcon: false, icon: <></> },
        { textColor: theme.palette.error.main, hasIcon: false, icon: <></> },
        {
            textColor: theme.palette.text.disabled,
            hasIcon: true,
            icon: <SyncIcon fontSize={"small"} color={"disabled"} />,
        },
        {
            textColor: theme.palette.text.disabled,
            hasIcon: true,
            icon: <CompleteIcon fontSize={"small"} color={"success"} />,
        },
        {
            textColor: theme.palette.warning.main,
            hasIcon: true,
            icon: <SyncIcon fontSize={"small"} color={"warning"} />,
        },
        {
            textColor: theme.palette.warning.main,
            hasIcon: true,
            icon: <SyncProblemIcon fontSize={"small"} color={"warning"} />,
        },
        { textColor: theme.palette.text.disabled, hasIcon: true, icon: <BrokenSync fontSize={"small"} color={"disabled"} /> },
    ];
    const SEPARATOR = " - ";
    const backgroundColor = theme.palette.secondary.main;
    const textColor = theme.palette.getContrastText(backgroundColor);

    const showDeleteButton = isNotInitializationTicket(selectedTicket) && (IsDemoMode() || !TicketValidator.IsReadOnly(selectedTicket));
    const modeTickets = ticketList.filter((t) => (t.isDemoTicket === undefined ? false : t.isDemoTicket) === IsDemoMode());

    const CURRENT_OPTIONS: [string, number][] = [
        ["LAST 2 WEEKS", 0],
        ["LAST 31 DAYS", 0],
    ];
    const CURRENT_OFFSETS = [14, 31];

    const currentDate = dayjs().startOf("day");
    const offsetDates = CURRENT_OFFSETS.map((o) => currentDate.subtract(o, "days"));
    const selectedFilter = HasValue(context.viewMonth) ? context.viewMonth : CURRENT_OPTIONS[0][0];
    const currentFilter = CURRENT_OPTIONS.findIndex(([t]) => t === selectedFilter);
    const latestDate = currentFilter >= 0 ? currentDate.add(1, "year") : dayjs(context.viewMonth + "/01", TICKET_DATE_FORMAT).startOf("month");
    const earliestDate = currentFilter >= 0 ? offsetDates[currentFilter] : dayjs(context.viewMonth + "/01", TICKET_DATE_FORMAT).endOf("month");

    const selectedTickets = modeTickets.filter(
        (t) => dayjs(t.date).isBetween(latestDate, earliestDate, "day", "[]") || !TicketValidator.IsReadOnly(t) || t.id === selectedTicket.id
    );

    type sortedTicketType = {
        sortOrder: string;
        ticketStatus: TicketStatus;
        ticket: TicketExtended;
    };

    const sortedTickets = selectedTickets
        .map((ticket: TicketExtended) => {
            let ticketStatus = TicketStatus.Complete;
            if (!isLastRevision(ticketList, ticket)) {
                ticketStatus = TicketStatus.Superseded;
            } else if (TicketValidator.IsStale(ticket)) {
                ticketStatus = TicketStatus.Synced;
            } else if (!TicketValidator.IsReadOnly(ticket) && TicketValidator.validate(aircraftList, ticketList, ticket).filter((t) => t.level === ErrorLevel.Error).length > 0) {
                ticketStatus = TicketStatus.Incomplete;
            } else if (ticket.id > 0 && (ticket.hasLocalChanges === undefined || !ticket.hasLocalChanges)) {
                ticketStatus = TicketStatus.Synced;
            } else if (ticket.pushAttempts === FAILED_SYNC || ticket.pushAttempts === DUPLICATE_TICKET) {
                ticketStatus = TicketStatus.Failed;
            } else if (ticket.pushAttempts === PARTIAL_SYNC) {
                ticketStatus = TicketStatus.Partial;
            } else if (TicketValidator.IsReadOnly(ticket)) {
                ticketStatus = TicketStatus.Signed;
            }

            const ticketNumber = getTicketNumber(ticket);

            const sortOrder = (ticketStatus === TicketStatus.Synced || ticketStatus === TicketStatus.Superseded ? "0" : "1") + ticket.date + ticketNumber; // this bubbles all incomplete ticket to the top

            return { sortOrder, ticketStatus, ticket } as sortedTicketType;
        })
        .sort((a, b) => {
            return a.sortOrder.localeCompare(b.sortOrder) * -1;
        });

    const ticketSelectionOptions = sortedTickets.map((option: sortedTicketType) => {
        const ticketDate = dayjs(option.ticket.date);
        const dateText = ticketDate.isValid() ? ticketDate.format(TICKET_DATE_FORMAT).toLocaleUpperCase() : "?";
        const ticketNumber = getTicketNumber(option.ticket);
        const companyText = option.ticket.company_id ? findById(companyList, option.ticket.company_id)?.selectName : "?";

        const displayText = `${dateText}${SEPARATOR}${ticketNumber}${SEPARATOR}${companyText}`;

        return (
            <MenuItem sx={{ color: STATUS_FORMATS[option.ticketStatus].textColor }} key={option.ticket.id} value={getTicketNumber(option.ticket)}>
                <ListItemText primary={displayText} />
                {STATUS_FORMATS[option.ticketStatus].hasIcon && <ListItemIcon sx={{ pl: 2 }}>{STATUS_FORMATS[option.ticketStatus].icon}</ListItemIcon>}
            </MenuItem>
        );
    });

    const getMonthSelections = () => {
        const monthCounts: [string, number][] = CURRENT_OPTIONS.slice();

        for (const ticket of modeTickets) {
            const ticketDate = HasValue(ticket.date) ? dayjs(ticket.date) : dayjs();

            offsetDates.forEach((offsetDate, i) => {
                if (ticketDate.isBetween(currentDate, offsetDate, "day", "[]")) {
                    monthCounts[i][1]++;
                }
            });

            const ticketMonth = ticketDate.format(MONTH_SELECTION_FORMAT).toLocaleUpperCase();

            const count = monthCounts.find(([countMonth]) => countMonth === ticketMonth);

            if (count) {
                count[1]++;
            } else {
                monthCounts.push([ticketMonth, 1]);
            }
        }

        return monthCounts;
    };

    const addATicket = () => {
        context.setShowAddTicketDialogue(true);
    };

    const deleteATicket = () => {
        context.deleteSelectedTicket();
    };

    const ticketNumberOnChange = (ticketNumber: string) => {
        const foundTicket = ticketList.find((t) => getTicketNumber(t) === ticketNumber) ?? createInitializationTicket();

        bookmarkTicket(foundTicket);

        context.setSelectedTicketId(foundTicket.id);
    };

    function getTicketDisplayText(ticketNumber: string) {
        const ticket: TicketExtended | undefined = ticketList?.find((t) => getTicketNumber(t) === ticketNumber);

        if (!ticket) return "??????";

        if (isOnMediumScreen) {
            const ticketDate = dayjs(ticket.date);
            const dateText = ticketDate.isValid() ? ticketDate.format(TICKET_DATE_FORMAT).toLocaleUpperCase() : "?";
            return `${dateText}${SEPARATOR}${ticketNumber}`;
        }

        return ticketNumber;
    }

    function handleMonthChange(dateString: string | null) {
        if (dateString && monthSelections.findIndex((s) => s[0] === dateString) < CURRENT_OPTIONS.length) {
            localStorage.setItem(MONTH_FILTER_PREFERENCE_KEY, dateString);
        }
        context.setViewMonth(dateString ?? CURRENT_OPTIONS[0][0]);
    }

    const monthSelections = getMonthSelections();

    const monthStrings = monthSelections.map(([m, c]) => {
        const monthText = `${m}`;
        const countText = `(${c} TICKET${c !== 1 ? "S" : ""})`;
        return (
            <MenuItem key={m} value={m}>
                <ListItemText primary={monthText} />
                &nbsp;
                <ListItemText sx={{ textAlign: "right" }} primary={countText} />
            </MenuItem>
        );
    });

    const monthValue = monthSelections.find(([m]) => {
        return m === context.viewMonth ?? monthSelections[0];
    });

    const monthString = monthValue ? monthValue[0] : monthSelections[0][0];

    // TODO oof, so ugly
    function getMonthDisplayText(month: string): React.ReactNode {
        const optionIndex = CURRENT_OPTIONS.findIndex(([option]) => month === option);
        if (optionIndex >= 0) {
            return ["2 WEEKS", "31 DAYS"][optionIndex];
        }
        return month;
    }

    const selectControlSx = {
        mt: -1,
        color: textColor,
        ".MuiSvgIcon-root ": {
            fill: `${textColor} !important`,
        },
    };

    const buttonSpacing = 2;
    const selectValueSx = {
        pt: 1,
        pb: 1,
        pl: 2,
        pr: 2,
        ml: buttonSpacing,
        borderRadius: 1,
        backgroundColor: backgroundColor,
        color: textColor,
        fontWeight: "bold",
    };

    return (
        <Box sx={{ mt: 3, mb: 2, display: "flex", alignItems: "center" }}>
            {monthSelections.length > 1 && (
                <Select
                    variant="standard"
                    disableUnderline
                    value={monthString}
                    sx={selectControlSx}
                    renderValue={(month) => {
                        return <Typography sx={selectValueSx}>{getMonthDisplayText(month)}</Typography>;
                    }}
                    onChange={(e) => {
                        handleMonthChange(e.target.value);
                    }}>
                    {monthStrings}
                </Select>
            )}
            {sortedTickets.length > 0 && (
                <Select
                    variant="standard"
                    disableUnderline
                    value={isNotInitializationTicket(selectedTicket) ? getTicketNumber(selectedTicket) : getTicketNumber(sortedTickets[0].ticket)}
                    sx={{ ...selectControlSx }}
                    renderValue={(ticketNumber) => {
                        return <Typography sx={selectValueSx}>{getTicketDisplayText(ticketNumber)}</Typography>;
                    }}
                    onChange={(e) => {
                        ticketNumberOnChange(e.target.value);
                    }}>
                    {ticketSelectionOptions}
                </Select>
            )}
            {isOnMediumScreen && (
                <Button variant="contained" startIcon={<AddTicketIcon />} color="secondary" onClick={addATicket} sx={{ mb: 1, ml: buttonSpacing }}>
                    NEW
                </Button>
            )}
            {!isOnMediumScreen && (
                <Button variant="contained" color="secondary" onClick={addATicket} sx={{ mb: 1, ml: buttonSpacing }}>
                    <AddTicketIcon />
                </Button>
            )}
            {isOnMediumScreen && showDeleteButton && (
                <Button variant="contained" startIcon={<DeleteTicketIcon />} color="secondary" onClick={deleteATicket} sx={{ mb: 1, ml: buttonSpacing }}>
                    DELETE
                </Button>
            )}
            {!isOnMediumScreen && showDeleteButton && (
                <Button variant="contained" color="secondary" onClick={deleteATicket} sx={{ mb: 1, ml: buttonSpacing }}>
                    <DeleteTicketIcon />
                </Button>
            )}
        </Box>
    );
}

export function TicketSort(a: TicketExtended, b: TicketExtended): number {
    const aDate = dayjs(a.date);
    const bDate = dayjs(b.date);
    const aReadOnly = TicketValidator.IsReadOnly(a);
    const bReadOnly = TicketValidator.IsReadOnly(b);

    // sort incomplete tickets to the top
    if (aReadOnly !== bReadOnly) {
        return aReadOnly ? 1 : -1;
    }

    if ((aDate.isValid() && bDate.isValid() && aDate.isSame(bDate)) || (!aDate.isValid() && !bDate.isValid())) {
        return a.ticket_num.localeCompare(b.ticket_num);
    }

    if (aDate.isValid() && bDate.isValid()) {
        return aDate.isSameOrAfter(bDate) ? -1 : 1;
    }

    // one of the tickets has no date, must be a new ticket so sort it to the top
    return bDate.isValid() ? -1 : 1;
}
